import React, { useEffect, useState, useCallback } from "react";
import { Toaster, toast } from "sonner";
import DashboardShell from "../components/layout/DashboardShell";
import { Card, Pill } from "../components/common/Primitives";
import api, { formatApiError, API_BASE } from "../lib/api";
import { formatDateTime } from "../lib/datetime";
import { Download } from "lucide-react";

export default function UserInvoices() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/invoices");
      setItems(r.data?.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <DashboardShell>
      <Toaster richColors position="top-right" />
      <div className="max-w-[1100px] mx-auto px-6 lg:px-10 py-8">
        <div className="overline">BILLING</div>
        <h1 className="font-display text-3xl lg:text-4xl mt-1">My invoices</h1>
        <div className="text-sm text-ink-soft mt-1 mb-6">
          Every VenturaZ payment ends here — GST-compliant, downloadable, and archived for 7 years.
        </div>

        <Card testId="user-invoices-card">
          {loading ? (
            <div className="text-sm text-ink-muted italic py-4">Loading…</div>
          ) : items.length === 0 ? (
            <div className="text-sm text-ink-muted italic py-4" data-testid="user-invoices-empty">
              You don't have any invoices yet. Once you complete a plan checkout, the invoice will show up here automatically.
            </div>
          ) : (
            <div className="divide-y divide-navy/[0.06]">
              {items.map((inv) => (
                <div key={inv.invoice_id} className="py-3 flex items-center justify-between gap-3" data-testid={`user-invoice-${inv.invoice_id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="font-mono font-bold text-sm">{inv.invoice_number}</span>
                      <Pill tone={inv.status === "PAID" ? "success" : "muted"}>{inv.status}</Pill>
                      {inv.coupon_code && <Pill tone="gold">Coupon: {inv.coupon_code}</Pill>}
                    </div>
                    <div className="text-[11px] font-mono text-ink-muted">
                      {(inv.line_items || [])[0]?.description} · {formatDateTime(inv.issued_at)}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <div className="text-right">
                      <div className="font-heading text-ink">₹{(inv.total_inr || 0).toLocaleString("en-IN")}</div>
                    </div>
                    <a href={`${API_BASE}/invoices/${inv.invoice_id}/pdf`}
                      target="_blank" rel="noopener noreferrer"
                      className="px-2 py-1 rounded border border-navy/15 text-[11px] font-heading text-ink hover:border-gold hover:text-[#8A6600] inline-flex items-center gap-1"
                      data-testid={`user-invoice-pdf-${inv.invoice_id}`}>
                      <Download size={12} /> PDF
                    </a>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </DashboardShell>
  );
}
